import { Asset } from '@tassat/fred-definitions';
import {
    AccountBalanceMap,
    AccountBalanceStats,
    AccountBalancesStatsParam,
    AccountPositionRecordAssetExtended,
    AllowedTransactionType,
    SecurityCurrencyAssetPair
} from '@tassat/fred-extended-definitions';
import { AccountBalancesCurrencyTileProps } from '@tassat/text/src/AccountBalancesCurrencyTile/types';
import { WalletTotalBalanceTileStats } from '@tassat/text/src/WalletTotalBalanceTile/types';
import { formatAsAsset, formatAsAssetNoSign } from '@tassat/utility';
import Decimal from 'decimal.js';

interface RowData {
    asset: string;
    currentBalance: string;
    totalBalances: null;
    equivalentValue: string | null;
    assetPrice: string | null;
    symbol: string;
}

export const getAccountBalancesTileData = (
    accountPositionBalance: AccountBalancesStatsParam,
    isCollateral?: boolean
): WalletTotalBalanceTileStats[] => {
    return accountPositionBalance.reduce<WalletTotalBalanceTileStats[]>((acc, accountPosition) => {
        const ccyData = Object.values(accountPosition.ccy)[0];
        const securityData = Object.values(accountPosition.security);

        // Define CCY balance tile data -- omitted entirely when the firm has no balance entry for this currency
        const ccyBalances = ccyData ? [
            {
                availableBalanceTitle: `Total Available ${accountPosition.curCode} Balance`,
                availableBalance: formatAsAsset(ccyData.availableBalance, ccyData.asset, 'symbol'),
                currentBalanceTitle: `Total Current ${accountPosition.curCode} Balance`,
                currentBalance: formatAsAsset(ccyData.currentBalance, ccyData.asset, 'symbol'),
                key: accountPosition.curCode,
                collateral: false
            }
        ] : [];

        // Define Security asset types balance tiles data
        const securityBalances = securityData.map((securityItem) => ({
            availableBalanceTitle: `Total Available ${securityItem.ticker} Balance`,
            availableBalance: formatAsAssetNoSign(securityItem.availableBalance, securityItem.asset, true),
            availableBottomDescription: `Equivalent ${accountPosition.curCode} Value: ${formatAsAsset(securityItem.equivalentAvailableBalance, ccyData?.asset, 'symbol')}`,
            currentBalanceTitle: `Total Current ${securityItem.ticker} Balance`,
            currentBalance: formatAsAssetNoSign(securityItem.currentBalance, securityItem.asset, true),
            currentBottomDescription: `Equivalent ${accountPosition.curCode} Value: ${formatAsAsset(securityItem.equivalentCurrentBalance, ccyData?.asset, 'symbol')}`,
            key: securityItem.ticker,
            collateral: false
        }));

        const collateralBalances = securityData
            .filter((securityItem) => securityItem.asset?.AllowedTransactions?.split(',')
                .map(t => t.trim())
                .includes(AllowedTransactionType.Collateral))
            .map((securityItem) => ({
                totalPledgeAmountTitle: `Posted ${securityItem.ticker} Collateral`,
                totalPledgeAmount: formatAsAssetNoSign(securityItem.totalPledgeAmount, securityItem.asset, true),
                totalPledgeAmountBottomDescription: `Equivalent ${accountPosition.curCode} Value: ${formatAsAsset(securityItem.equivalentTotalPledgeAmount, ccyData?.asset, 'symbol')}`,
                totalPledgeReceivedTitle: `Received ${securityItem.ticker} Collateral`,
                totalPledgeReceived: formatAsAssetNoSign(securityItem.totalPledgeReceived, securityItem.asset, true),
                totalPledgeReceivedBottomDescription: `Equivalent ${accountPosition.curCode} Value: ${formatAsAsset(securityItem.equivalentTotalPledgeReceived, ccyData?.asset, 'symbol')}`,
                key: securityItem.ticker,
                collateral: true
            }));

        return (isCollateral ? [ ...acc, ...ccyBalances, ...securityBalances, ...collateralBalances ]
            : [ ...acc, ...ccyBalances, ...securityBalances ]) as WalletTotalBalanceTileStats[];
    }, []);
};

const getAllowedTransactionTypes = (allowedTransactions?: string): Set<string> => new Set(allowedTransactions?.split(',')
    .map(t => t.trim()) ?? []);

export const getAssetBalancesTileData = (
    assetPairs: SecurityCurrencyAssetPair[],
    accountBalancesStats: AccountBalancesStatsParam
): {
    currency: AccountBalancesCurrencyTileProps['currency'];
    accountCurrencyStatistics: AccountBalancesCurrencyTileProps['accountCurrencyStatistics'];
}[] => {
    type TileEntry = { currency: AccountBalancesCurrencyTileProps['currency']; accountCurrencyStatistics: AccountBalancesCurrencyTileProps['accountCurrencyStatistics'] };

return accountBalancesStats.reduce<TileEntry[]>((acc, accountPosition) => {
        const ccyData = Object.values(accountPosition.ccy)[0];
        const currencyAsset = assetPairs.find(assetPair => assetPair.currency?.Ticker === accountPosition.curCode)?.currency;
        const ccyAllowedTypes = getAllowedTransactionTypes(currencyAsset?.AllowedTransactions);

        // Define CCY balance tile data -- omitted entirely when the firm has no balance entry for this currency
        const ccyBalance = !ccyData ? null : {
            currency: { AlphabeticCode: accountPosition.curCode },
            accountCurrencyStatistics: {
                headerPrimaryStats: [
                    {
                        title: 'Available Balance - My Accounts',
                        statistic: formatAsAsset(ccyData.availableBalance, currencyAsset, 'symbol')
                    },
                    {
                        title: 'Current Balance - My Accounts',
                        statistic: formatAsAsset(ccyData.currentBalance, currencyAsset, 'symbol')
                    }
                ],
                detailStats: [
                    ...(ccyAllowedTypes.has(AllowedTransactionType.Withdrawal) ? [
                        {
                            title: 'Today\'s Withdrawals',
                            statistic: formatAsAsset(ccyData.todaysWithdrawalsAmount || 0, currencyAsset, 'symbol'),
                            statisticCount: ccyData.todaysWithdrawalsCount || 0
                        }
                    ] : []),
                    ...(ccyAllowedTypes.has(AllowedTransactionType.Transfer) ? [
                        {
                            title: 'Today\'s Sends',
                            statistic: formatAsAsset(ccyData.todaysSendsAmount || 0, currencyAsset, 'symbol'),
                            statisticCount: ccyData.todaysSendsCount || 0
                        }
                    ] : []),
                    ...(ccyAllowedTypes.has(AllowedTransactionType.Deposit) ? [
                        {
                            title: 'Today\'s Deposits',
                            statistic: formatAsAsset(ccyData.todaysDepositsAmount || 0, currencyAsset, 'symbol'),
                            statisticCount: ccyData.todaysDepositsCount || 0
                        }
                    ] : [])
                ]
            }
        };

        // One security balance tile per security asset in this accountPosition
        const securityBalances = Object.entries(accountPosition.security)
            .flatMap(([ assetId, securityData ]) => {
            const pair = assetPairs.find(p => String(p.security?.AssetId) === assetId);
            if (!pair?.security) return [];

            const secAllowedTypes = getAllowedTransactionTypes(pair.security.AllowedTransactions);

            return [
            {
                currency: { AlphabeticCode: pair.security.Ticker },
                accountCurrencyStatistics: {
                    headerPrimaryStats: [
                        {
                            title: 'Available Balance - My Accounts',
                            statistic: !securityData ?
                                '0'
                                : formatAsAsset(securityData?.availableBalance, pair.security, 'symbol', undefined, true)
                        },
                        {
                            title: 'Equivalent USD Value',
                            statistic: !securityData ?
                                '$0.00'
                                : formatAsAsset(securityData?.equivalentAvailableBalance, currencyAsset, 'symbol')
                        },
                        {
                            title: 'Current Balance - My Accounts',
                            statistic: !securityData ?
                                '0'
                                : formatAsAsset(securityData?.currentBalance, pair.security, 'symbol', undefined, true)
                        },
                        {
                            title: 'Equivalent USD Value',
                            statistic: !securityData ?
                                '$0.00'
                                : formatAsAsset(securityData?.equivalentCurrentBalance, currencyAsset, 'symbol')
                        }
                    ],
                    detailStats: [
                        // TODO: Change multipliation calculation here for proper truncation and large number handling
                        ...(secAllowedTypes.has(AllowedTransactionType.Buy) ? [
                            {
                                title: 'Today\'s Buys',
                                statistic: !securityData ?
                                    '0'
                                    : formatAsAsset(securityData?.todaysBuysAmount, pair.security, 'ticker', undefined, true),
                                statisticCount: securityData?.todaysBuysCount || 0,
                                equivalentValue: `Equivalent USD ${formatAsAsset((!securityData ? 0 : Number(securityData?.todaysBuysAmount) * Number(securityData?.price)), currencyAsset, 'symbol')}`
                            }
                        ] : []),
                        ...(secAllowedTypes.has(AllowedTransactionType.Subscribe) ? [
                            {
                                title: 'Today\'s Subscriptions',
                                statistic: !securityData ?
                                    '0'
                                    : formatAsAsset(securityData?.todaysSubscriptionsAmount, pair.security, 'ticker', undefined, true),
                                statisticCount: securityData?.todaysSubscriptionsCount || 0,
                                equivalentValue: `Equivalent USD ${formatAsAsset((!securityData ? 0 : Number(securityData?.todaysSubscriptionsAmount) * Number(securityData?.price)), currencyAsset, 'symbol')}`
                            }
                        ] : []),
                        ...(secAllowedTypes.has(AllowedTransactionType.Sell) ? [
                            {
                                title: 'Today\'s Sells',
                                statistic: !securityData ?
                                    '0'
                                    : formatAsAsset(securityData?.todaysSellsAmount, pair.security, 'ticker', undefined, true),
                                statisticCount: securityData?.todaysSellsCount || 0,
                                equivalentValue: `Equivalent USD ${formatAsAsset((!securityData ? 0 : Number(securityData?.todaysSellsAmount) * Number(securityData?.price)), currencyAsset, 'symbol')}`
                            }
                        ] : []),
                        ...(secAllowedTypes.has(AllowedTransactionType.Redeem) ? [
                            {
                                title: 'Today\'s Redemptions',
                                statistic: !securityData ?
                                    '0'
                                    : formatAsAsset(securityData?.todaysRedemptionsAmount, pair.security, 'ticker', undefined, true),
                                statisticCount: securityData?.todaysRedemptionsCount || 0,
                                equivalentValue: `Equivalent USD ${formatAsAsset((!securityData ? 0 : Number(securityData?.todaysRedemptionsAmount) * Number(securityData?.price)), currencyAsset, 'symbol')}`
                            }
                        ] : []),
                        ...(secAllowedTypes.has(AllowedTransactionType.Transfer) ? [
                            {
                                title: 'Today\'s Sends',
                                statistic: !securityData ?
                                    '0'
                                    : formatAsAsset(securityData?.todaysSendsAmount, pair.security, 'ticker', undefined, true),
                                statisticCount: securityData?.todaysSendsCount || 0,
                                equivalentValue: `Equivalent USD ${formatAsAsset((!securityData ? 0 : Number(securityData?.todaysSendsAmount) * Number(securityData?.price)), currencyAsset, 'symbol')}`
                            }
                        ] : [])
                    ]
                }
            }
        ];
        });

        return [
            ...acc,
            ...(ccyBalance ? [ccyBalance] : []),
            ...securityBalances
        ] as TileEntry[];
    }, []);
};

export const getAccountBalancesWithAssetId = (
    accPositions: AccountPositionRecordAssetExtended[],
    isSecurityAsset: boolean = false,
    retrieveTransactionStats: boolean = false,
    retrieveCollateralStats: boolean = false
): AccountBalanceMap => {
    return accPositions.reduce((acc, item) => {
        const assetId = item.AssetId;
        const parsedAvailable = new Decimal(item['AvailablePosition'] ?? 0);
        const parsedCurrent = new Decimal(item['CurrentPosition'] ?? 0);
        const parsedTotalPledgeAmount = new Decimal(item['TotalPledgeAmount'] ?? 0);
        const parsedTotalPledgeReceived = new Decimal(item['TotalPledgeReceived'] ?? 0);
        const parsedTodaysWithdrawalsAmount = retrieveTransactionStats ? new Decimal(item['TodaysWithdrawalsAmount'] ?? 0) : 0;
        const parsedTodaysWithdrawalsCount = retrieveTransactionStats ? Number(item['TodaysWithdrawalsCount']) : undefined;
        const parsedTodaysSendsAmount = retrieveTransactionStats ? new Decimal(item['TodaysSendsAmount'] ?? 0) : 0;
        const parsedTodaysSendsCount = retrieveTransactionStats ? Number(item['TodaysSendsCount']) : undefined;
        const parsedTodaysBuysAmount = retrieveTransactionStats ? new Decimal(item['TodaysBuysAmount'] ?? 0) : 0;
        const parsedTodaysBuysCount = retrieveTransactionStats ? Number(item['TodaysBuysCount']) : undefined;
        const parsedTodaysSubscriptionsAmount = retrieveTransactionStats ? new Decimal(item['TodaysSubscriptionsAmount'] ?? 0) : 0;
        const parsedTodaysSubscriptionsCount = retrieveTransactionStats ? Number(item['TodaysSubscriptionsCount']) : undefined;
        const parsedTodaysSellsAmount = retrieveTransactionStats ? new Decimal(item['TodaysSellsAmount'] ?? 0) : 0;
        const parsedTodaysSellsCount = retrieveTransactionStats ? Number(item['TodaysSellsCount']) : undefined;
        const parsedTodaysRedemptionsAmount = retrieveTransactionStats ? new Decimal(item['TodaysRedemptionsAmount'] ?? 0) : 0;
        const parsedTodaysRedemptionsCount = retrieveTransactionStats ? Number(item['TodaysRedemptionsCount']) : undefined;
        const parsedTodaysDepositsAmount = retrieveTransactionStats ? new Decimal(item['TodaysDepositsAmount'] ?? 0) : 0;
        const parsedTodaysDepositsCount = retrieveTransactionStats ? Number(item['TodaysDepositsCount']) : undefined;
        const parsedPendingWithdrawalAmount = retrieveTransactionStats ? new Decimal(item['PendingWithdrawalAmount'] ?? 0) : 0;
        const parsedPendingWithdrawalCount = retrieveTransactionStats ? Number(item['PendingWithdrawalCount']) : undefined;
        const parsedPendingApprovalSendAmount = retrieveTransactionStats ? new Decimal(item['PendingApprovalSendAmount'] ?? 0) : 0;
        const parsedPendingApprovalSendCount = retrieveTransactionStats ? Number(item['PendingApprovalSendCount']) : undefined;
        const parsedPendingBuyAmount = retrieveTransactionStats ? new Decimal(item['PendingBuyAmount'] ?? 0) : 0;
        const parsedPendingBuyCount = retrieveTransactionStats ? Number(item['PendingBuyCount']) : undefined;
        const parsedPendingSubscriptionAmount = retrieveTransactionStats ? new Decimal(item['PendingSubscriptionAmount'] ?? 0) : 0;
        const parsedPendingSubscriptionCount = retrieveTransactionStats ? Number(item['PendingSubscriptionCount']) : undefined;
        const parsedPendingRedemptionAmount = retrieveTransactionStats ? new Decimal(item['PendingRedemptionAmount'] ?? 0) : 0;
        const parsedPendingRedemptionCount = retrieveTransactionStats ? Number(item['PendingRedemptionCount']) : undefined;
        const parsedPendingSellAmount = retrieveTransactionStats ? new Decimal(item['PendingSellAmount'] ?? 0) : 0;
        const parsedPendingSellCount = retrieveTransactionStats ? Number(item['PendingSellCount']) : undefined;
        const parsedPendingApprovalBuyAmount = retrieveTransactionStats ? new Decimal(item['PendingApprovalBuyAmount'] ?? 0) : 0;
        const parsedPendingApprovalBuyCount = retrieveTransactionStats ? Number(item['PendingApprovalBuyCount']) : undefined;
        const parsedPendingApprovalRedemptionAmount = retrieveTransactionStats ? new Decimal(item['PendingApprovalRedemptionAmount'] ?? 0) : 0;
        const parsedPendingApprovalRedemptionCount = retrieveTransactionStats ? Number(item['PendingApprovalRedemptionCount']) : undefined;
        const parsedPendingApprovalSellAmount = retrieveTransactionStats ? new Decimal(item['PendingApprovalSellAmount'] ?? 0) : 0;
        const parsedPendingApprovalSellCount = retrieveTransactionStats ? Number(item['PendingApprovalSellCount']) : undefined;
        const parsedPendingApprovalSubscriptionAmount = retrieveTransactionStats ? new Decimal(item['PendingApprovalSubscriptionAmount'] ?? 0) : 0;
        const parsedPendingApprovalSubscriptionCount = retrieveTransactionStats ? Number(item['PendingApprovalSubscriptionCount']) : undefined;
        const parsedPendingApprovalWithdrawalAmount = retrieveTransactionStats ? new Decimal(item['PendingApprovalWithdrawalAmount'] ?? 0) : 0;
        const parsedPendingApprovalWithdrawalCount = retrieveTransactionStats ? Number(item['PendingApprovalWithdrawalCount']) : undefined;

        if (acc[assetId]) {
            acc[assetId] = {
                ...acc[assetId],
                availableBalance: Decimal.add(new Decimal(acc[assetId]['availableBalance']), parsedAvailable),
                currentBalance: Decimal.add(new Decimal(acc[assetId]['currentBalance']), parsedCurrent),
                ...(retrieveTransactionStats &&
                    {
                        todaysWithdrawalsAmount: Decimal.add(new Decimal(acc[assetId]['todaysWithdrawalsAmount']), parsedTodaysWithdrawalsAmount),
                        todaysWithdrawalsCount: acc[assetId]['todaysWithdrawalsCount'] + parsedTodaysWithdrawalsCount,
                        todaysSendsAmount: Decimal.add(new Decimal(acc[assetId]['todaysSendsAmount']), parsedTodaysSendsAmount),
                        todaysSendsCount: acc[assetId]['todaysSendsCount'] + parsedTodaysSendsCount,
                        todaysBuysAmount: Decimal.add(new Decimal(acc[assetId]['todaysBuysAmount']), parsedTodaysBuysAmount),
                        todaysBuysCount: acc[assetId]['todaysBuysCount'] + parsedTodaysBuysCount,
                        todaysSubscriptionsAmount: Decimal.add(new Decimal(acc[assetId]['todaysSubscriptionsAmount']), parsedTodaysSubscriptionsAmount),
                        todaysSubscriptionsCount: acc[assetId]['todaysSubscriptionsCount'] + parsedTodaysSubscriptionsCount,
                        todaysSellsAmount: Decimal.add(new Decimal(acc[assetId]['todaysSellsAmount']), parsedTodaysSellsAmount),
                        todaysSellsCount: acc[assetId]['todaysSellsCount'] + parsedTodaysSellsCount,
                        todaysRedemptionsAmount: Decimal.add(new Decimal(acc[assetId]['todaysRedemptionsAmount']), parsedTodaysRedemptionsAmount),
                        todaysRedemptionsCount: acc[assetId]['todaysRedemptionsCount'] + parsedTodaysRedemptionsCount,
                        todaysDepositsAmount: Decimal.add(new Decimal(acc[assetId]['todaysDepositsAmount']), parsedTodaysDepositsAmount),
                        todaysDepositsCount: acc[assetId]['todaysDepositsCount'] + parsedTodaysDepositsCount,
                        pendingWithdrawalAmount: Decimal.add(new Decimal(acc[assetId]['pendingWithdrawalAmount']), parsedPendingWithdrawalAmount),
                        pendingWithdrawalCount: acc[assetId]['pendingWithdrawalCount'] + parsedPendingWithdrawalCount,
                        pendingApprovalSendAmount: Decimal.add(new Decimal(acc[assetId]['pendingApprovalSendAmount']), parsedPendingApprovalSendAmount),
                        pendingApprovalSendCount: acc[assetId]['pendingApprovalSendCount'] + parsedPendingApprovalSendCount,
                        pendingBuyAmount: Decimal.add(new Decimal(acc[assetId]['pendingBuyAmount']), parsedPendingBuyAmount),
                        pendingBuyCount: acc[assetId]['pendingBuyCount'] + parsedPendingBuyCount,
                        pendingSubscriptionAmount: Decimal.add(new Decimal(acc[assetId]['pendingSubscriptionAmount']), parsedPendingSubscriptionAmount),
                        pendingSubscriptionCount: acc[assetId]['pendingSubscriptionCount'] + parsedPendingSubscriptionCount,
                        pendingRedemptionAmount: Decimal.add(new Decimal(acc[assetId]['pendingRedemptionAmount']), parsedPendingRedemptionAmount),
                        pendingRedemptionCount: acc[assetId]['pendingRedemptionCount'] + parsedPendingRedemptionCount,
                        pendingSellAmount: Decimal.add(new Decimal(acc[assetId]['pendingSellAmount']), parsedPendingSellAmount),
                        pendingSellCount: acc[assetId]['pendingSellCount'] + parsedPendingSellCount,
                        pendingApprovalBuyAmount: Decimal.add(new Decimal(acc[assetId]['pendingApprovalBuyAmount']), parsedPendingApprovalBuyAmount),
                        pendingApprovalBuyCount: acc[assetId]['pendingApprovalBuyCount'] + parsedPendingApprovalBuyCount,
                        pendingApprovalRedemptionAmount: Decimal.add(new Decimal(acc[assetId]['pendingApprovalRedemptionAmount']), parsedPendingApprovalRedemptionAmount),
                        pendingApprovalRedemptionCount: acc[assetId]['pendingApprovalRedemptionCount'] + parsedPendingApprovalRedemptionCount,
                        pendingApprovalSellAmount: Decimal.add(new Decimal(acc[assetId]['pendingApprovalSellAmount']), parsedPendingApprovalSellAmount),
                        pendingApprovalSellCount: acc[assetId]['pendingApprovalSellCount'] + parsedPendingApprovalSellCount,
                        pendingApprovalSubscriptionAmount: Decimal.add(new Decimal(acc[assetId]['pendingApprovalSubscriptionAmount']), parsedPendingApprovalSubscriptionAmount),
                        pendingApprovalSubscriptionCount: acc[assetId]['pendingApprovalSubscriptionCount'] + parsedPendingApprovalSubscriptionCount,
                        pendingApprovalWithdrawalAmount: Decimal.add(new Decimal(acc[assetId]['pendingApprovalWithdrawalAmount']), parsedPendingApprovalWithdrawalAmount),
                        pendingApprovalWithdrawalCount: acc[assetId]['pendingApprovalWithdrawalCount'] + parsedPendingApprovalWithdrawalCount
                    }
                ),
                ...(retrieveCollateralStats &&
                    {
                        totalPledgeAmount: Decimal.add(new Decimal(acc[assetId]['totalPledgeAmount']), parsedTotalPledgeAmount),
                        totalPledgeReceived: Decimal.add(new Decimal(acc[assetId]['totalPledgeReceived']), parsedTotalPledgeReceived)
                    }
                ),
                // TODO: Change multipliation calculation here for proper truncation and large number handling
                ...(isSecurityAsset && {
                    equivalentTotalPledgeAmount: Decimal.mul(parsedTotalPledgeAmount, new Decimal(item.Price ?? 0))
                        .add(new Decimal(acc[assetId]['equivalentTotalPledgeAmount'])),
                    equivalentTotalPledgeReceived: Decimal.mul(parsedTotalPledgeReceived, new Decimal(item.Price ?? 0))
                        .add(new Decimal(acc[assetId]['equivalentTotalPledgeReceived'])),
                    equivalentAvailableBalance: Decimal.mul(parsedAvailable, new Decimal(item.Price ?? 0))
                        .add(new Decimal(acc[assetId]['equivalentAvailableBalance'])),
                    equivalentCurrentBalance: Decimal.mul(parsedCurrent, new Decimal(item.Price ?? 0))
                        .add(new Decimal(acc[assetId]['equivalentCurrentBalance']))
                })
            };
        } else {
            acc[assetId] = {
                availableBalance: parsedAvailable,
                currentBalance: parsedCurrent,
                asset: item.Asset,
                ...(retrieveTransactionStats &&
                    {
                        todaysWithdrawalsAmount: parsedTodaysWithdrawalsAmount,
                        todaysWithdrawalsCount: parsedTodaysWithdrawalsCount,
                        todaysSendsAmount: parsedTodaysSendsAmount,
                        todaysSendsCount: parsedTodaysSendsCount,
                        todaysBuysAmount: parsedTodaysBuysAmount,
                        todaysBuysCount: parsedTodaysBuysCount,
                        todaysSubscriptionsAmount: parsedTodaysSubscriptionsAmount,
                        todaysSubscriptionsCount: parsedTodaysSubscriptionsCount,
                        todaysSellsAmount: parsedTodaysSellsAmount,
                        todaysSellsCount: parsedTodaysSellsCount,
                        todaysRedemptionsAmount: parsedTodaysRedemptionsAmount,
                        todaysRedemptionsCount: parsedTodaysRedemptionsCount,
                        todaysDepositsAmount: parsedTodaysDepositsAmount,
                        todaysDepositsCount: parsedTodaysDepositsCount,
                        pendingWithdrawalAmount: parsedPendingWithdrawalAmount,
                        pendingWithdrawalCount: parsedPendingWithdrawalCount,
                        pendingApprovalSendAmount: parsedPendingApprovalSendAmount,
                        pendingApprovalSendCount: parsedPendingApprovalSendCount,
                        pendingBuyAmount: parsedPendingBuyAmount,
                        pendingBuyCount: parsedPendingBuyCount,
                        pendingSubscriptionAmount: parsedPendingSubscriptionAmount,
                        pendingSubscriptionCount: parsedPendingSubscriptionCount,
                        pendingRedemptionAmount: parsedPendingRedemptionAmount,
                        pendingRedemptionCount: parsedPendingRedemptionCount,
                        pendingSellAmount: parsedPendingSellAmount,
                        pendingSellCount: parsedPendingSellCount,
                        pendingApprovalBuyAmount: parsedPendingApprovalBuyAmount,
                        pendingApprovalBuyCount: parsedPendingApprovalBuyCount,
                        pendingApprovalRedemptionAmount: parsedPendingApprovalRedemptionAmount,
                        pendingApprovalRedemptionCount: parsedPendingApprovalRedemptionCount,
                        pendingApprovalSellAmount: parsedPendingApprovalSellAmount,
                        pendingApprovalSellCount: parsedPendingApprovalSellCount,
                        pendingApprovalSubscriptionAmount: parsedPendingApprovalSubscriptionAmount,
                        pendingApprovalSubscriptionCount: parsedPendingApprovalSubscriptionCount,
                        pendingApprovalWithdrawalAmount: parsedPendingApprovalWithdrawalAmount,
                        pendingApprovalWithdrawalCount: parsedPendingApprovalWithdrawalCount
                    }
                ),
                ...(retrieveCollateralStats &&
                    {
                        totalPledgeAmount: parsedTotalPledgeAmount,
                        totalPledgeReceived: parsedTotalPledgeReceived
                    }
                ),
                ...(isSecurityAsset &&
                // TODO: Change multipliation calculation here for proper truncation and large number handling
                    {
                        equivalentTotalPledgeAmount: Decimal.mul(parsedTotalPledgeAmount, new Decimal(item.Price ?? 0)),
                        equivalentTotalPledgeReceived: Decimal.mul(parsedTotalPledgeReceived, new Decimal(item.Price ?? 0)),
                        equivalentAvailableBalance: Decimal.mul(parsedAvailable, new Decimal(item.Price ?? 0)),
                        equivalentCurrentBalance: Decimal.mul(parsedCurrent, new Decimal(item.Price ?? 0)),
                        price: item.Price ?? '',
                        ticker: item.Ticker ?? ''
                    }
                )
            };
        }

        return acc;
    }, {});
};

function findMatchingAssetByType(assets, dataType) {
    const assetIds = new Set(Object.keys(dataType));

    return assets.find(asset => assetIds.has(asset.AssetId));
}

export const generateRowDataForAccountBalances = (
    accountBalancesStats: AccountBalancesStatsParam,
    assets: Asset[]
): { list: RowData[]; totalBalance: string }[] => {
    if (!Object.keys(accountBalancesStats[0]?.ccy || {}).length && !Object.keys(accountBalancesStats[0]?.security || {}).length) {
        return [
            {
                list: [],
                totalBalance: ''
            }
        ];
    }

    return accountBalancesStats.reduce<{ list: RowData[]; totalBalance: string }[]>((acc, stat) => {
        const {
            curCode,
            symbol,
            ccy,
            security
        } = stat;

        const mappedData: RowData[] = [];
        const ccyAsset = findMatchingAssetByType(assets, ccy);

        const ccyTotal = (Object.values(ccy) as AccountBalanceStats[])
            .reduce((sum, ccyItem) => {
                mappedData.push({
                    asset: curCode,
                    currentBalance: formatAsAsset(ccyItem.currentBalance, ccyAsset, 'symbol'),
                    totalBalances: null,
                    equivalentValue: null,
                    assetPrice: null,
                    symbol
                });

                return Decimal.add(sum, new Decimal(ccyItem.currentBalance ?? 0));
            }, new Decimal(0.0));

        const equivalentSecurityTotal = (Object.values(security) as AccountBalanceStats[])
            .reduce((sum, secItem) => {
                mappedData.push({
                    asset: secItem.ticker || 'Unknown',
                    currentBalance: formatAsAsset(Number(secItem.currentBalance ?? 0), secItem.asset, 'symbol', undefined, true),
                    totalBalances: null,
                    equivalentValue: formatAsAsset(Number(secItem.equivalentCurrentBalance ?? 0), ccyAsset, 'symbol'),
                    assetPrice: formatAsAsset(secItem.price, ccyAsset, 'symbol'),
                    symbol
                });

                return Decimal.add(sum, new Decimal(secItem.equivalentCurrentBalance ?? 0));
            }, new Decimal(0.0));

        let total = new Decimal(0.0);
        total = Decimal.add(ccyTotal, equivalentSecurityTotal);

        acc.push({
            list: mappedData,
            totalBalance: formatAsAsset(total, ccyAsset, 'symbol')
        });

        return acc;
    }, []);
};

export const getAccountPLedgeWithAssetId = (
    accPositions: AccountPositionRecordAssetExtended[]
): AccountBalanceMap => {
    return accPositions.reduce((acc, item) => {
        const assetId = item.AssetId;
        const parsedTotalPledgeReceived = new Decimal(item['TotalPledgeReceived'] ?? 0);
        const parsedTotalPledgeAmount = new Decimal(item['TotalPledgeAmount'] ?? 0);

        if (acc[assetId]) {
            acc[assetId] = {
                ...acc[assetId],
                totalPledgeReceived: Decimal.add(new Decimal(acc[assetId]['TotalPledgeReceived'] ?? 0), parsedTotalPledgeReceived),
                totalPledgeAmount: Decimal.add(new Decimal(acc[assetId]['TotalPledgeAmount'] ?? 0), parsedTotalPledgeAmount),
                equivalentTotalPledgeReceived: Decimal.mul(parsedTotalPledgeReceived, new Decimal(item.Price ?? 0))
                    .add(new Decimal(acc[assetId]['equivalentTotalPledgeReceived'])),
                equivalentTotalPledgeAmount: Decimal.mul(parsedTotalPledgeAmount, new Decimal(item.Price ?? 0))
                    .add(new Decimal(acc[assetId]['equivalentTotalPledgeAmount']))
            };
        } else {
            acc[assetId] = {
                totalPledgeReceived: parsedTotalPledgeReceived,
                totalPledgeAmount: parsedTotalPledgeAmount,
                asset: item.Asset,
                equivalentTotalPledgeReceived: Decimal.mul(parsedTotalPledgeReceived, new Decimal(item.Price ?? 0)),
                equivalentTotalPledgeAmount: Decimal.mul(parsedTotalPledgeAmount, new Decimal(item.Price ?? 0)),
                price: item.Price ?? '',
                ticker: item.Ticker ?? ''
            };
        }

        return acc;
    }, {});
};

export const getAccountPledgeTileData = (accountPositionBalance: Array<{
    curCode: string;
    symbol: string;
    security: AccountBalanceMap;
}>): WalletTotalBalanceTileStats[] => {
    return accountPositionBalance.reduce<WalletTotalBalanceTileStats[]>((acc, accountPosition) => {
        const securityData = Object.values(accountPosition.security)
            .filter((securityItem) => securityItem.asset?.IsCollateral !== false);

        return [
            ...acc,
            ...securityData.map((securityItem) => {
                const asset = {
                    ...securityItem.asset,
                    Ticker: securityItem.asset?.Ticker ?? '',
                    Symbol: securityItem.asset?.Symbol ?? '',
                    Curcode: securityItem.asset?.Curcode ?? '',
                    DecimalPoint: securityItem.asset?.DecimalPoint ?? 2
                };

                return ({
                    availableBalanceTitle: `Total ${securityItem.ticker} Received`,
                    availableBalance: formatAsAssetNoSign(securityItem.totalPledgeReceived, securityItem.asset, true),
                    availableBottomDescription: `Equivalent ${accountPosition.curCode} Value: ${accountPosition.symbol}${formatAsAsset(securityItem.equivalentTotalPledgeReceived, asset, 'symbol')}`,
                    currentBalanceTitle: `Total ${securityItem.ticker} Sent`,
                    currentBalance: formatAsAssetNoSign(securityItem.totalPledgeAmount, securityItem.asset, true),
                    currentBottomDescription: `Equivalent ${accountPosition.curCode} Value: ${accountPosition.symbol}${formatAsAsset(securityItem.equivalentTotalPledgeAmount, asset, 'symbol')}`,
                    key: securityItem.ticker ?? ''
                });
            })
        ];
    }, []);
};